export function handleError(e: any) {
    const errorData = e.response
    if (errorData) {
        if (errorData.status === 400) {
            return [[errorData.data]]
        } else if (errorData.status === 401) {
            // backend error messages
            // Access denied. Invalid token.
            // Access denied. No token provided.
            // Access denied. Invalid API key.
            return [['Please make sure you are logged in.']]
        } else if (errorData.status === 404) {
            return [[errorData.data]]
        } else if (errorData.status === 409) {
            return [['Conflict.']]
        } else if (errorData.status === 429) {
            return [[errorData.data]]
        } else {
            // 500
            return [['Unexpected result. Verify your function call and retry.']]
        }
    } else {
        return [['Unexpected error. Try function with multiple tickers.']]
    }
}

export function checkToken(symbols: string[], demoTickersList: string[]) {
    const returnedObj = {
        error: null,
        decodedToken: null,
        subscriptionType: null as SubscriptionType,
    }
    const token = window.localStorage.getItem('token')
    let decodedToken = null
    if (token) {
        let payload = token.split('.')[1]
        payload = window.atob(payload)
        decodedToken = JSON.parse(payload)
        returnedObj.decodedToken = decodedToken
        returnedObj.subscriptionType = decodedToken.subscriptionType || null
        if (
            !decodedToken.isSubscribed &&
            symbols.some((r) => !demoTickersList.includes(r))
        ) {
            returnedObj.error = [
                [
                    'Please purchase a subscription to use Wisesheets with any symbol.',
                ],
            ]
        }
    } else {
        returnedObj.error = [
            ['Please login to use the WISE/WISEPRICE function.'],
        ]
    }
    return returnedObj
}

export function excelDateToJSDate(excelDate) {
    if (
        typeof excelDate === 'string' &&
        (excelDate.includes('-') || excelDate.includes('/'))
    ) {
        let dateString = excelDate
        if (excelDate.includes('/')) {
            const parts = excelDate.split('/')
            let day, month, year

            // Auto-detect format based on first part
            if (parseInt(parts[0]) > 12) {
                // DD/MM/YYYY format
                day = parts[0].padStart(2, '0')
                month = parts[1].padStart(2, '0')
                year = parts[2]
            } else if (parseInt(parts[1]) > 12) {
                // MM/DD/YYYY format
                month = parts[0].padStart(2, '0')
                day = parts[1].padStart(2, '0')
                year = parts[2]
            } else {
                // Ambiguous case - default to MM/DD/YYYY for backward compatibility
                month = parts[0].padStart(2, '0')
                day = parts[1].padStart(2, '0')
                year = parts[2]
            }

            dateString = `${year}-${month}-${day}`
        }
        const date = new Date(dateString + 'T00:00:00')
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
    } else {
        const excelEpoch = new Date(1899, 11, 30)
        const date = new Date(excelEpoch.getTime() + excelDate * 86400 * 1000)
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
    }
}

type SubscriptionType = 'REGULAR' | 'PREMIUM' | 'ENTERPRISE' | null

class RateLimiter {
    private requests: number[]
    private windowMs: number

    constructor(windowMs: number = 30000) {
        this.requests = []
        this.windowMs = windowMs
    }

    private getRateLimitForSubscription(
        subscriptionType: SubscriptionType
    ): number {
        if (subscriptionType === 'ENTERPRISE') {
            return 3000
        } else if (subscriptionType === 'PREMIUM') {
            return 2000
        } else {
            return 1000
        }
    }

    async checkLimit(
        subscriptionType: SubscriptionType,
        weight: number = 1
    ): Promise<boolean> {
        const now = Date.now()
        this.requests = this.requests.filter(
            (time) => now - time < this.windowMs
        )

        const maxRequests = this.getRateLimitForSubscription(subscriptionType)
        if (this.requests.length + weight > maxRequests) {
            return false
        }

        // Add 'weight' number of requests to the rate limiter
        for (let i = 0; i < weight; i++) {
            this.requests.push(now)
        }
        return true
    }

    async checkAndApplyLimit(
        subscriptionType: SubscriptionType,
        weight: number = 1
    ): Promise<string | null> {
        if (await this.checkLimit(subscriptionType, weight)) {
            return null
        }
        return 'Too many simultaneous requests.'
    }
}

export const wiseRateLimiter = new RateLimiter(30000)
export const wisePriceRateLimiter = new RateLimiter(30000)
export const wiseFundsRateLimiter = new RateLimiter(30000)
export const wiseOptionsRateLimiter = new RateLimiter(30000)
